import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  CustomFieldObjectTypes,
  SubStepTypes,
  WorkflowObjectTypes,
} from '@generated/graphql';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import {
  ACCOUNT_RULE_FIELDS,
  ENTITY_RULE_FIELDS,
  GOAL_RULE_FIELDS,
} from '@ovComponents/4-module/widgets/customFieldsDisplay/customFieldsDisplay.queries';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { isEmpty } from 'lodash-es';
import { useContext, useState } from 'react';
import { usePermission } from '../../../../../hooks/usePermission';
import { getEnabledCustomFields } from '../utils';
import { ApprovalVisual } from './approval.visual';

/**
 * Folds a fetched user/goal/account into approval state so custom-field trigger
 * rules can read entity attributes (e.g. account `type` === RLIF). `id` is
 * remapped so merging several entities doesn't leave them fighting over it.
 */
const mergeObjects =
  (object: any, idKey: 'userId' | 'goalId' | 'accountId') => (prev: any) => {
    if (!object) return prev;
    const { id, customFields = [], ...rest } = object;
    return {
      ...prev,
      ...rest,
      [idKey]: id,
      customFields: [...prev.customFields, ...customFields],
    };
  };

const FETCH_USER = (derivedCustomFields?: string[]) => gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        ${ENTITY_RULE_FIELDS}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format objectType }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;
const FETCH_ACCOUNT = (derivedCustomFields?: string[]) => gql`
  query fetchAccount($accountId: SafeString!) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        id: stringId
        ${ACCOUNT_RULE_FIELDS}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format objectType }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const FETCH_GOAL = (derivedCustomFields?: string[]) => gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        ${GOAL_RULE_FIELDS}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format objectType }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;
export const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
      }
    }
  }
`;
const UPDATE_ACCOUNT = gql`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

export const Approval = ({
  options,
  onNext,
  stepLoading,
  objectId,
  objectType,
}: {
  options: any;
  onNext: (branchId?: string) => void;
  stepLoading: boolean;
  objectId?: string;
  objectType?: WorkflowObjectTypes;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData } = useContext(WorkflowContext);
  const [derivedCustomFields, setDerivedCustomFields] = useState<CustomField[]>(
    [],
  );
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] = useState<
    string[]
  >([]);
  const enabledCustomFields = getEnabledCustomFields(options);
  const canReadCustomFields = usePermission('read:custom_fields');
  const userId =
    workflowData.currentUserId ||
    (objectType === WorkflowObjectTypes.User ? objectId : undefined);
  const goalId =
    workflowData.currentGoalId ||
    (objectType === WorkflowObjectTypes.Goal ? objectId : undefined);
  const accountId =
    workflowData.currentAccountId ||
    (objectType === WorkflowObjectTypes.Account ? objectId : undefined);
  const [approvalData, setApprovalData] = useState<any>({
    customFields: [],
    userId: '',
    goalId: '',
    accountId: '',
  });
  const { loading: CustomFieldLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.Approval,
          organizationId: activeOrganization?.id ?? undefined,
          keys: enabledCustomFields,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !canReadCustomFields || enabledCustomFields.length === 0,
    fetchPolicy: 'no-cache',
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customOptions: any[] = [];
      const keyItems = res?.fetchCustomFields?.customFields.map(
        (a: CustomField) => a.key,
      );
      Object.keys(options).forEach((key: string) => {
        if (
          options[key]?.customField &&
          options[key].enabled &&
          keyItems?.includes(key)
        ) {
          customOptions.push(options[key]);
        }
      });
      const entityCustomFieldKeys =
        customOptions
          .filter(
            (i: any) =>
              i.customField.objectType === CustomFieldObjectTypes.Entity,
          )
          .map((k: any) => k.customField.key) || [];
      const goalCustomFieldKeys =
        customOptions
          .filter(
            (i: any) =>
              i.customField.objectType === CustomFieldObjectTypes.Goal,
          )
          .map((k: any) => k.customField.key) || [];
      const accountCustomFieldKeys =
        customOptions
          .filter(
            (i: any) =>
              i.customField.objectType === CustomFieldObjectTypes.Account,
          )
          .map((k: any) => k.customField.key) || [];

      const newCustomFieldsKeys = [...derivedCustomFieldsKeys];
      if (!isEmpty(entityCustomFieldKeys) && userId) {
        newCustomFieldsKeys.push(...entityCustomFieldKeys);
        await fetchUser({
          variables: {
            userId,
          },
          query: entityCustomFieldKeys
            ? FETCH_USER(entityCustomFieldKeys)
            : undefined,
          onCompleted: (res: any) =>
            setApprovalData(mergeObjects(res.fetchUser?.user, 'userId')),
        });
      }

      if (!isEmpty(goalCustomFieldKeys) && goalId) {
        newCustomFieldsKeys.push(...goalCustomFieldKeys);
        await fetchGoal({
          variables: {
            goalId,
          },
          query: goalCustomFieldKeys
            ? FETCH_GOAL(goalCustomFieldKeys)
            : undefined,
          onCompleted: (res: any) =>
            setApprovalData(mergeObjects(res.fetchGoal?.goal, 'goalId')),
        });
      }

      if (!isEmpty(accountCustomFieldKeys) && accountId) {
        newCustomFieldsKeys.push(...accountCustomFieldKeys);
        await fetchAccount({
          variables: {
            accountId,
          },
          query: accountCustomFieldKeys
            ? FETCH_ACCOUNT(accountCustomFieldKeys)
            : undefined,
          onCompleted: (res: any) =>
            setApprovalData(
              mergeObjects(res.fetchAccount?.account, 'accountId'),
            ),
        });
      }

      setDerivedCustomFieldsKeys(newCustomFieldsKeys);
    },
  });

  const [fetchUser, { loading: userLoading }] = useLazyQuery(FETCH_USER(), {
    variables: { userId: workflowData.currentUserId },
    fetchPolicy: 'no-cache',
  });

  const [fetchGoal, { loading: goalLoading }] = useLazyQuery(FETCH_GOAL(), {
    variables: { goalId: workflowData.currentGoalId },
    fetchPolicy: 'no-cache',
  });

  const [fetchAccount, { loading: accountLoading }] = useLazyQuery(
    FETCH_ACCOUNT(),
    {
      variables: { accountId: workflowData.currentAccountId },
      fetchPolicy: 'no-cache',
    },
  );

  const [updateUser] = useMutation(UPDATE_USER);

  const [updateGoal] = useMutation(UPDATE_GOAL);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT);

  return (
    <ApprovalVisual
      options={options}
      continueFunc={async (branchId: string) => {
        if (approvalData?.customFields) {
          const entityCustomFieldKeys =
            approvalData.customFields
              .filter(
                (i: any) =>
                  i.customField.objectType === CustomFieldObjectTypes.Entity,
              )
              .map((k: any) => k.key) || [];
          const goalCustomFieldKeys =
            approvalData.customFields
              .filter(
                (i: any) =>
                  i.customField.objectType === CustomFieldObjectTypes.Goal,
              )
              .map((k: any) => k.key) || [];
          const accountCustomFieldKeys =
            approvalData.customFields
              .filter(
                (i: any) =>
                  i.customField.objectType === CustomFieldObjectTypes.Account,
              )
              .map((k: any) => k.key) || [];
          if (!isEmpty(entityCustomFieldKeys) && userId) {
            await updateUser({
              variables: {
                input: {
                  userId,
                  customFields: modifyUpdatingCustomFields(
                    approvalData?.customFields,
                    derivedCustomFields ?? [],
                    entityCustomFieldKeys,
                  ),
                },
              },
            });
          }

          if (!isEmpty(goalCustomFieldKeys) && goalId) {
            await updateGoal({
              variables: {
                input: {
                  goalId,
                  customFields: modifyUpdatingCustomFields(
                    approvalData?.customFields,
                    derivedCustomFields ?? [],
                    goalCustomFieldKeys,
                  ),
                },
              },
            });
          }

          if (!isEmpty(accountCustomFieldKeys) && accountId) {
            await updateAccount({
              variables: {
                input: {
                  accountId,
                  customFields: modifyUpdatingCustomFields(
                    approvalData?.customFields,
                    derivedCustomFields ?? [],
                    accountCustomFieldKeys,
                  ),
                },
              },
            });
          }
        }
        await onNext(branchId);
      }}
      loading={
        stepLoading ||
        userLoading ||
        goalLoading ||
        accountLoading ||
        CustomFieldLoading
      }
      approvalData={approvalData}
      updateApproval={setApprovalData}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
    />
  );
};

export default Approval;
